import { Component, type ReactNode } from 'react'

/**
 * Wraps the wizard's current-step content so a render crash in ONE step can
 * never blank the whole card (Tej 2026-09-12: a supplier uploaded docs, hit
 * Next, and got a frozen white card — a step threw and, with no boundary, React
 * unmounted the subtree to nothing). A blank card is the worst signup dead-end:
 * the account is already created, so the user is stranded signed-in-incomplete
 * with no visible way out.
 *
 * Instead we show the error + a recovery control. `key={step}` at the call site
 * resets the boundary on every step change, so a transient crash never sticks
 * the user on the error screen after they move on. The message is surfaced (not
 * hidden) so the actual failure is diagnosable from a real user's screen.
 */
export class StepErrorBoundary extends Component<
  { children: ReactNode; onRecover: () => void },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null }

  static getDerivedStateFromError(error: Error): { error: Error } {
    return { error }
  }

  componentDidCatch(error: Error): void {
    // eslint-disable-next-line no-console
    console.error('[signup-wizard] step render crashed:', error)
  }

  render(): ReactNode {
    if (!this.state.error) return this.props.children
    return (
      <div data-testid="signup-step-error">
        <h1 className="font-serif text-[clamp(1.75rem,4vw,2.5rem)] font-bold text-ink tracking-h1 leading-tight">
          This step hit a snag
        </h1>
        <p className="mt-2 text-sm text-navy-500">
          Your account is already saved — nothing you entered is lost. Continue to
          the catalog; you can finish anything remaining (documents, stock
          connection) from your account at any time.
        </p>
        <pre className="mt-4 max-h-40 overflow-auto rounded-sm bg-navy-050 p-3 text-[11px] leading-snug text-navy-600 whitespace-pre-wrap break-words">
          {this.state.error.message || String(this.state.error)}
        </pre>
        <button
          type="button"
          onClick={() => { this.setState({ error: null }); this.props.onRecover() }}
          className="btn-primary mt-6 h-[52px] w-full text-base"
          data-testid="signup-step-error-continue"
        >
          Continue to my account
        </button>
      </div>
    )
  }
}
